import { useSelector } from 'react-redux';
import {
  replaceDomainForCacheKey,
  useErrorOnNotOkFetchye,
  oneErrorOnNotOkFetchye,
} from '@americanexpress/fetchye-amex';
import { ignoreHeadersByKey } from 'fetchye';
import {
  getBdaasAirportLoungesUrl,
  getBdaasAuthKey,
} from '@/selectors/bdaasSelectors';
import { getOneCmsLocaleLoungesUrl } from '@/selectors/oneCmsSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import { getIsMarketWithTranslations } from '@/selectors/localeSelectors';
import { getWalletIsLoading, getUserWallet } from '@/selectors/walletSelector';
import { Card } from '@/constants';
import { getCenturionOrActiveToken } from '@/helpers/accountHelper';
import { useCenturionOrActiveToken } from '../memberHooks';

export const useBdaasAirportLounges = () => {
  // if (global.mock) {
  //   return {
  //     data: {
  //       body: global.mock.lounges,
  //     },
  //   };
  // }
  const routeParams = useRouteParams();
  const url = useSelector(getBdaasAirportLoungesUrl(routeParams));
  const isWalletLoading = useSelector(getWalletIsLoading);
  const accountToken = useCenturionOrActiveToken();
  const isCenturionProduct = routeParams?.product === Card.Centurion;

  const abortController = new AbortController();
  const timeout = 6000; // 6 seconds

  setTimeout(() => {
    abortController.abort();
  }, timeout);

  const {
    data: lounges,
    isLoading: fetchyeLoungesLoading,
    error: fetchyeLoungesError,
  } = useErrorOnNotOkFetchye(
    url,
    {
      mapKeyToCacheKey: replaceDomainForCacheKey('bdaas'),
      mapOptionsToKey: ignoreHeadersByKey(['Authorization']),
      credentials: 'include',
      defer: isCenturionProduct
        ? !accountToken && !routeParams?.airportCode
        : !routeParams?.airportCode,
      headers: {
        ...(accountToken && {
          account_token: accountToken,
        }),
      },
      signal: abortController.signal,
    },
    {
      reporting: {
        url,
        collectionMethod: 'useBdaasAirportLounges',
        moduleId: 'travel-lounge-finder-root',
      },
    }
  );

  return {
    data: lounges,
    error: fetchyeLoungesError,
    isLoading:
      isWalletLoading ||
      fetchyeLoungesLoading ||
      (!lounges && !fetchyeLoungesError),
  };
};

export const loadBdaasAirportLounges =
  (product, airportCode) => async (dispatch, getState) => {
    const params = { product, airportCode };
    const url = getBdaasAirportLoungesUrl(params)(getState());
    const bdaasAuthKey = getBdaasAuthKey(getState());
    const wallet = getUserWallet(getState());
    const accountToken = getCenturionOrActiveToken(wallet);

    return dispatch(
      oneErrorOnNotOkFetchye(
        url,
        {
          mapKeyToCacheKey: replaceDomainForCacheKey('bdaas'),
          mapOptionsToKey: ignoreHeadersByKey(['Authorization']),
          credentials: 'include',
          headers: {
            ...(accountToken && {
              account_token: accountToken,
            }),
            ...(bdaasAuthKey && {
              Authorization: `Basic ${Buffer.from(`${bdaasAuthKey}`).toString('base64')}`,
            }),
          },
        },
        {
          reporting: {
            url,
            collectionMethod: 'loadBdaasAirportLounges',
            moduleId: 'travel-lounge-finder-root',
          },
        }
      )
    );
  };

export const useOneCmsLocaleAirportLounges = () => {
  const isMarketWithTranslations = useSelector(getIsMarketWithTranslations);
  const url = useSelector(getOneCmsLocaleLoungesUrl);
  return useErrorOnNotOkFetchye(
    url,
    {
      defer: !isMarketWithTranslations,
      mapKeyToCacheKey: replaceDomainForCacheKey('oneCms'),
    },
    {
      reporting: {
        url,
        collectionMethod: 'useOneCmsLocaleAirportLounges',
        moduleId: 'travel-lounge-finder-root',
      },
    }
  );
};

export const loadOneCmsLocaleAirportLounges = async (dispatch, getState) => {
  if (!getIsMarketWithTranslations(getState())) {
    return null;
  }
  const url = getOneCmsLocaleLoungesUrl(getState());
  return dispatch(
    oneErrorOnNotOkFetchye(
      url,
      {
        mapKeyToCacheKey: replaceDomainForCacheKey('oneCms'),
      },
      {
        reporting: {
          url,
          collectionMethod: 'loadOneCmsLocaleAirportLounges',
          moduleId: 'travel-lounge-finder-root',
        },
      }
    )
  );
};
